// react
import { useEffect, useRef, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';

const AlgorithmInputAdvancedDialog = ({
  open,
  onClose,
  subscription,
  specificationData,
  update,
}) => {
  const { t } = useTranslation();
  const [input, setInput] = useState(
    JSON.stringify(subscription?.AlgorithmInput ?? {}, null, 2),
  );
  const [error, setError] = useState(false);
  const [errorLine, setErrorLine] = useState(-1);
  const [errorMessage, setErrorMessage] = useState(''); // To store the JSON error message
  const [disableFields, setDisableFields] = useState(false);
  const textAreaRef = useRef(null);
  const lineNumberRef = useRef(null);

  // Turn a character position into line/column (1-based)
  const getLineColFromPos = (text, pos) => {
    const upto = text.slice(0, pos);
    const lines = upto.split('\n');
    const line = lines.length; // 1-based
    const col = (lines[lines.length - 1] || '').length + 1; // 1-based
    return { line, col };
  };

  // useEffect(() => {
  //   setInput(JSON.stringify(subscription?.AlgorithmInput ?? {}, null, 2));
  // }, [onClose]);

  const handleInputChange = (event) => {
    setInput(event.target.value);
    setError(false);
    setErrorLine(null);
  };

  useEffect(() => {
    const validateJSON = () => {
      try {
        JSON.parse(input);
        setError(false);
        setErrorLine(null);
        setErrorMessage('');
      } catch (e) {
        // Prefer "position N" (standard Chrome/Node message)
        const posMatch = /position\s+(\d+)/i.exec(e.message);
        let line = null;
        if (posMatch) {
          const pos = parseInt(posMatch[1], 10);
          ({ line } = getLineColFromPos(input, pos));
        } else {
          // Fallback if the engine actually provides "line N"
          const lineMatch = /line\s+(\d+)/i.exec(e.message);
          if (lineMatch) line = parseInt(lineMatch[1], 10);
        }

        setError(true);
        setErrorLine(line);

        // Clean, user-facing message
        const where = line != null ? `Line ${line}: ` : '';
        setErrorMessage(`${where}${e.message}`);
      }
    };
    const id = setTimeout(() => validateJSON(), 200); // 200–300ms feels nice
    return () => clearTimeout(id);
  }, [input]);

  useEffect(() => {
    const syncScroll = () => {
      if (lineNumberRef.current && textAreaRef.current) {
        lineNumberRef.current.scrollTop = textAreaRef.current.scrollTop;
      }
    };

    const textArea = textAreaRef.current;
    if (textArea) {
      textArea.addEventListener('scroll', syncScroll);
      return () => {
        textArea.removeEventListener('scroll', syncScroll);
      };
    }
  }, []);

  const getLineNumbers = (inputText) => {
    const lines = inputText.split('\n');
    return lines.map((_, idx) => (
      <div
        key={idx}
        style={{
          textAlign: 'right',
          paddingRight: '10px',
          lineHeight: '21px',
          color: idx + 2 === errorLine ? 'red' : 'inherit',
          fontSize: '16px',
        }}
      >
        {idx + 2}
      </div>
    ));
  };

  const handleSubmit = async () => {
    const updatedAlgorithmInput = JSON.parse(input);
    const { DataInput, UserInput, DisplayName } = subscription;

    const putData = {
      DataInput,
      UserInput,
      AlgorithmInput: updatedAlgorithmInput,
      DisplayName,
    };

    try {
      setDisableFields(true);
      update(putData); // Assuming your RTK Query hook uses update
      // const result = await v({id: subscription.JobSubscriptionId, patch: putData}).unwrap(); // Assuming your RTK Query hook uses unwrap
      setDisableFields(false);
      onClose(); // Close modal on success
    } catch (error) {
      console.error('Failed to update subscription:', error);
      setErrorMessage('Update failed: ' + error.message); // Show error message in UI
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>
          {t('glossary.algorithmInput', { ns: 'buildingOptimization' })}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid
              sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
              size={6}>
              <div
                style={{ position: 'relative', display: 'flex', flexGrow: 1 }}
              >
                <div
                  ref={lineNumberRef}
                  style={{
                    userSelect: 'none',
                    paddingTop: '10px',
                    marginTop: '25px', // Make sure this matches TextField's visual offset
                    overflow: 'hidden',
                  }}
                >
                  {getLineNumbers(input)}
                </div>
                <TextField
                  fullWidth
                  multiline
                  variant="outlined"
                  label="JSON Input"
                  value={input}
                  onChange={handleInputChange}
                  error={error}
                  disabled={disableFields}
                  helperText={
                    error ? errorMessage || 'Invalid JSON' : 'Valid JSON data'
                  }
                  margin="normal"
                  rows={
                    input.split('\n').length < 35
                      ? 35
                      : input.split('\n').length
                  }
                  slotProps={{
                    htmlInput: {
                      spellCheck: 'false',
                      style: {
                        fontFamily: 'monospace',
                        lineHeight: '21px',
                        fontSize: '12px', // Match font size with line numbers for consistent layout
                        '::selection': {
                          backgroundColor: '#2196f3', // Custom selection color
                          color: '#fff', // Selection text color
                        },
                      },
                      ref: textAreaRef,
                    }
                  }}
                />
              </div>
            </Grid>
            <Grid
              sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              size={6}>
              <TextField
                fullWidth
                multiline
                variant="outlined"
                label="Read-Only JSON"
                value={
                  JSON.stringify(specificationData ?? {}, null, 2) ||
                  'Loading...'
                }
                margin="normal"
                rows={
                  input.split('\n').length < 35 ? 35 : input.split('\n').length
                }
                slotProps={{
                  input: {
                    readOnly: true,
                    style: {
                      fontSize: '12px',
                      fontFamily: 'monospace',
                      lineHeight: '21px', // Ensure line height here matches the line number div
                    },
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="text" disabled={disableFields}>
            {t('actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={error}>
            {t('actions.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlgorithmInputAdvancedDialog;
